// Mounts the sign-in screen for requests the API layer couldn't complete, and
// registers the requester that lets it ask (see api/auth-gate.ts).
//
// Separate from AuthProvider on purpose: that owns *who* is signed in, this
// owns the one piece of UI that asks them to. Sits at the app root so a refused
// request can prompt from any route, not just where a header happens to render.

import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import {
   resumeSignInPrompts,
   setSignInRequester,
   suppressSignInPrompts,
   type SignInRequestOptions,
} from 'api/auth-gate'
import SignInDialog from 'components/modals/sign-in-dialog'
import { trackSignInDismissed, trackSignInPrompt } from 'lib/analytics/events'

const HomeHref = '/'

type PendingRequest = {
   message: string
   options: SignInRequestOptions
   // Resolves the promise the waiting caller is holding: true once signed in,
   // false if the user dismissed the screen.
   resolve: (signedIn: boolean) => void
}

export function SignInGate({ children }: { children: ReactNode }) {
   const [request, setRequest] = useState<PendingRequest | null>(null)
   const navigate = useNavigate()
   const href = useRouterState({ select: (state) => state.location.href })
   // The page we sent the user to when they dismissed, so the redirect itself
   // isn't mistaken for them navigating somewhere new.
   const dismissedTo = useRef<string | null>(null)

   useEffect(() => {
      setSignInRequester(
         (message, options) =>
            new Promise<boolean>((resolve) => {
               trackSignInPrompt(options.source)
               setRequest({ message, options, resolve })
            })
      )
      return () => setSignInRequester(null)
   }, [])

   useEffect(() => {
      if (dismissedTo.current !== null && href !== dismissedTo.current) {
         // They've gone somewhere of their own accord — a new search is a new
         // intent, so it's allowed to ask again.
         dismissedTo.current = null
         resumeSignInPrompts()
      }
   }, [href])

   const settle = (signedIn: boolean) => {
      const options = request?.options
      request?.resolve(signedIn)
      setRequest(null)
      if (signedIn) {
         return
      }
      trackSignInDismissed(options?.source ?? 'api_gate')

      // A prompt the user asked for by clicking something leaves them exactly
      // where they were. The page behind it works — they were reading a word —
      // so navigating away would punish them for changing their mind, and
      // suppressing would make the next deliberate click do nothing at all.
      if (options && !options.strandsPage) return

      // The API-refusal case, which is the opposite. Dismissing has to lead
      // somewhere: every request the page behind the screen makes is refused,
      // so leaving the user there strands them on a page that can never load.
      // Home has no query to look up, so it renders without touching the API.
      //
      // Suppressing until they navigate again is what makes the dismissal
      // stick: a page's calls are staggered — the translation and session posts
      // only start once the search has settled — so each one refused afterwards
      // would re-open the screen they just closed.
      suppressSignInPrompts()
      dismissedTo.current = HomeHref
      void navigate({ to: HomeHref, search: {} })
   }

   return (
      <>
         {children}
         <SignInDialog
            open={request !== null}
            message={request?.message}
            onSignedIn={() => settle(true)}
            onClose={() => settle(false)}
         />
      </>
   )
}
